import { useEffect, useMemo, useRef, useState } from "react";
import { applyChromeSplitTone, resolveChromeSurfaceToneAtPoint } from "@/utils/chromeSplitTone";
import {
  consumeHomeHeroResetRequest,
  HOME_HERO_RESET_EVENT,
} from "@/utils/homeNavigation";

const DESKTOP_MEDIA = "(min-width: 768px)";
const MOBILE_HORIZONTAL_STAGE_MEDIA = "(max-width: 767px)";
const HOMEPAGE_CONTENT_LOADED_EVENT = "venuex:homepage-content-loaded";
const HANDOFF_SNAP_DISTANCE = 40;
const VERTICAL_SCROLL_LOCK_THRESHOLD = 1;
const MAX_EFFECTIVE_WHEEL_DELTA = 220;
const HORIZONTAL_WHEEL_MULTIPLIER = 1.14;
const VERTICAL_WHEEL_MULTIPLIER = 0.84;
const WHEEL_HANDOFF_SPILL_THRESHOLD = 2;
const WHEEL_DELTA_LINE_PX = 18;
const WHEEL_DELTA_PAGE_RATIO = 0.82;
const WHEEL_CADENCE_WINDOW_MS = 180;
const WHEEL_DIRECTION_WINDOW_MS = 240;
const WHEEL_CADENCE_BOOST = 1.35;
const WHEEL_DIRECTION_BOOST = 1.12;
const HORIZONTAL_SHORT_TRAVEL_EASING = 0.16;
const HORIZONTAL_LONG_TRAVEL_EASING = 0.21;
const VERTICAL_SHORT_TRAVEL_EASING = 0.12;
const VERTICAL_LONG_TRAVEL_EASING = 0.16;
const KEYBOARD_SCROLL_STEP_RATIO = 0.72;

function normalizeWheelDelta(event: WheelEvent) {
  const rawDelta = event.deltaY + event.deltaX;

  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return rawDelta * WHEEL_DELTA_LINE_PX;
  }

  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return rawDelta * window.innerHeight * WHEEL_DELTA_PAGE_RATIO;
  }

  return Math.sign(rawDelta) * Math.min(Math.abs(rawDelta), MAX_EFFECTIVE_WHEEL_DELTA);
}

function mixHexColor(from: string, to: string, progress: number) {
  const value = Math.min(Math.max(progress, 0), 1);
  const parse = (hex: string) => {
    const clean = hex.replace("#", "");
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16),
    };
  };

  const start = parse(from);
  const end = parse(to);
  const channel = (first: number, second: number) =>
    Math.round(first + (second - first) * value)
      .toString(16)
      .padStart(2, "0");

  return `#${channel(start.r, end.r)}${channel(start.g, end.g)}${channel(start.b, end.b)}`;
}

function bindMagneticLinks(root: ParentNode) {
  const cleanups = Array.from(root.querySelectorAll<HTMLElement>("[data-magnetic]")).map(
    (element) => {
      const onMove = (event: PointerEvent) => {
        const rect = element.getBoundingClientRect();
        const offsetX = ((event.clientX - rect.left) / rect.width - 0.5) * 14;
        const offsetY = ((event.clientY - rect.top) / rect.height - 0.5) * 12;

        element.style.setProperty("--magnetic-x", `${offsetX}px`);
        element.style.setProperty("--magnetic-y", `${offsetY}px`);
      };

      const onLeave = () => {
        element.style.setProperty("--magnetic-x", "0px");
        element.style.setProperty("--magnetic-y", "0px");
      };

      element.addEventListener("pointermove", onMove);
      element.addEventListener("pointerleave", onLeave);

      return () => {
        element.removeEventListener("pointermove", onMove);
        element.removeEventListener("pointerleave", onLeave);
      };
    },
  );

  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}

function isInteractiveTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest(
      'a, button, input, textarea, select, label, summary, [role="button"], [data-no-stage-drag]',
    ),
  );
}

export function useHorizontalStage(sectionIds: string[]) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [activeSectionId, setActiveSectionId] = useState(sectionIds[0] ?? "");
  const pointerXRef = useRef(0);
  const pointerYRef = useRef(0);
  const currentScrollRef = useRef(0);
  const targetScrollRef = useRef(0);
  const currentVerticalScrollRef = useRef(0);
  const targetVerticalScrollRef = useRef(0);
  const lastWheelTimestampRef = useRef(0);
  const lastWheelDirectionRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const sectionSelector = useMemo(
    () => sectionIds.map((id) => `#${id}`).join(", "),
    [sectionIds],
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    const root = document.getElementById("homeStage");

    if (!viewport || !root || !sectionIds.length) {
      return;
    }

    const desktopQuery = window.matchMedia(DESKTOP_MEDIA);
    const mobileHorizontalQuery = window.matchMedia(MOBILE_HORIZONTAL_STAGE_MEDIA);
    const storageKey = `venuex-horizontal-scroll:${window.location.pathname}`;
    const panels = Array.from(root.querySelectorAll<HTMLElement>("[data-panel]"));
    const parallaxItems = Array.from(root.querySelectorAll<HTMLElement>("[data-parallax-speed]"));
    const depthItems = Array.from(root.querySelectorAll<HTMLElement>("[data-depth]"));

    const continuityRail = root.querySelector<HTMLElement>("#continuityRail");
    const continuityBase = root.querySelector<HTMLElement>("#continuityBase");
    const continuityCard = root.querySelector<HTMLElement>("#continuityCard");
    const cardBackLayer = root.querySelector<HTMLElement>("#cardBackLayer");
    const cardFrontLayer = root.querySelector<HTMLElement>("#cardFrontLayer");
    const heroPanel = root.querySelector<HTMLElement>(".panel-hero");
    const identitySection = root.querySelector<HTMLElement>("#identity");
    const storyPanel = root.querySelector<HTMLElement>(".panel-story");
    const storyMediaShell = root.querySelector<HTMLElement>("#storyMediaShell");
    const storyTopOutgoing = root.querySelector<HTMLElement>("#storyTopOutgoing");
    const storyMiddleOutgoing = root.querySelector<HTMLElement>("#storyMiddleOutgoing");
    const storyMiddleIncoming = root.querySelector<HTMLElement>("#storyMiddleIncoming");
    const storyCopy = root.querySelector<HTMLElement>(".story-copy");
    const storyHeadlineMotion = root.querySelector<HTMLElement>(".story-headline-motion-inner");
    const storyHeadlineRailTarget = root.querySelector<HTMLElement>(".story-headline-rail-target");
    const motionSection = root.querySelector<HTMLElement>("#motion-system");
    const storyBottomAccent = root.querySelector<HTMLElement>("#storyBottomAccent");
    const storyBaseLayer = root.querySelector<HTMLElement>(".story-collage-base");
    const continuityImageShell = root.querySelector<HTMLElement>(".continuity-image-shell");
    const heroMoveLine = root.querySelector<HTMLElement>(".hero-line-move-inner");
    const heroAccentLine = root.querySelector<HTMLElement>(".hero-line--accent");
    const continuityBand = root.querySelector<HTMLElement>(".continuity-band");
    const chrome = root.querySelector<HTMLElement>(".identity-chrome");
    const chromeLogo = root.querySelector<HTMLElement>(".identity-chrome-logo");
    const chromeMenu = root.querySelector<HTMLElement>(".identity-chrome-menu");
    const servicesSection = root.querySelector<HTMLElement>(".panel-services");
    const servicesPinShell = root.querySelector<HTMLElement>("#servicesPinShell");

    let dragging = false;
    let dragStartX = 0;
    let dragStartTarget = 0;
    let touchDragging = false;
    let touchAxis: "undecided" | "vertical" | "horizontal" = "undecided";
    let touchLastX = 0;
    let touchLastY = 0;
    let hasRestoredPosition = false;
    let heroMoveTravel = 0;
    let heroMoveScrollSpan = 1;
    let storyPhraseTravel = 0;
    let storyPhraseStartScroll = 0;
    let mobileObserver: IntersectionObserver | null = null;

    pointerXRef.current = window.innerWidth * 0.5;
    pointerYRef.current = window.innerHeight * 0.5;

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);
    const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const maxVerticalScroll = () =>
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const isMenuOpen = () => root.dataset.menuOpen === "true";
    const isHorizontalViewportMode = () => desktopQuery.matches || mobileHorizontalQuery.matches;

    const readSavedPosition = () => {
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    const writeSavedPosition = (scrollLeft: number) => {
      try {
        const max = maxScroll();
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            left: Math.round(scrollLeft),
            progress: max > 0 ? scrollLeft / max : 0,
            viewportWidth: viewport.clientWidth,
          }),
        );
      } catch {
        // Ignore session storage failures.
      }
    };

    const updateCursor = (x: number, y: number) => {
      pointerXRef.current = x;
      pointerYRef.current = y;
      root.style.setProperty("--cursor-x", `${x}px`);
      root.style.setProperty("--cursor-y", `${y}px`);
    };

    const updateActiveSection = () => {
      if (isHorizontalViewportMode()) {
        const viewportCenter = viewport.scrollLeft + viewport.clientWidth * 0.5;
        const sections = Array.from(viewport.querySelectorAll<HTMLElement>(sectionSelector));
        let nextActive = sectionIds[0];
        let minDistance = Number.POSITIVE_INFINITY;

        sections.forEach((section) => {
          const center = section.offsetLeft + section.offsetWidth * 0.5;
          const distance = Math.abs(center - viewportCenter);

          if (distance < minDistance) {
            minDistance = distance;
            nextActive = section.id;
          }
        });

        setActiveSectionId(nextActive);
        return;
      }

      const sections = Array.from(document.querySelectorAll<HTMLElement>(sectionSelector));
      const viewportOffset = window.innerHeight * 0.28;
      let nextActive = sectionIds[0];
      let minDistance = Number.POSITIVE_INFINITY;

      sections.forEach((section) => {
        const distance = Math.abs(section.getBoundingClientRect().top - viewportOffset);

        if (distance < minDistance) {
          minDistance = distance;
          nextActive = section.id;
        }
      });

      setActiveSectionId(nextActive);
    };

    const measureHeroMove = () => {
      if (!heroPanel || !heroMoveLine || !continuityBand) {
        return;
      }

      heroMoveLine.style.transform = "translate3d(0, 0, 0)";
      const moveRect = heroMoveLine.getBoundingClientRect();
      const bandRect = continuityBand.getBoundingClientRect();
      const heroMoveStartLeft = moveRect.left;
      const heroMoveTargetLeft = Math.max(heroMoveStartLeft, bandRect.left - moveRect.width - 76);

      heroMoveTravel = Math.max(0, heroMoveTargetLeft - heroMoveStartLeft);
      heroMoveScrollSpan = Math.max(viewport.clientWidth * 0.34, heroMoveTravel * 0.9, 1);
    };

    const measureStoryPhrase = () => {
      if (!storyPanel || !storyHeadlineMotion || !storyHeadlineRailTarget) {
        return;
      }

      storyHeadlineMotion.style.transform = "translate3d(0, 0, 0)";
      const phraseRect = storyHeadlineMotion.getBoundingClientRect();
      const targetRect = storyHeadlineRailTarget.getBoundingClientRect();
      const phraseStartLeft = phraseRect.left;
      const phraseAbsoluteRight = viewport.scrollLeft + phraseRect.right;
      const phraseTargetLeft = Math.max(
        phraseStartLeft,
        targetRect.left - phraseRect.width + clamp(viewport.clientWidth * 0.28, 220, 360),
      );

      storyPhraseTravel = Math.max(0, phraseTargetLeft - phraseStartLeft);
      storyPhraseStartScroll = Math.max(
        storyPanel.offsetLeft,
        phraseAbsoluteRight - viewport.clientWidth + viewport.clientWidth * 0.68,
      );
    };

    const updateDepth = (globalProgress: number) => {
      const ratioX = pointerXRef.current / Math.max(window.innerWidth, 1) - 0.5;
      const ratioY = pointerYRef.current / Math.max(window.innerHeight, 1) - 0.5;

      depthItems.forEach((item) => {
        const depth = Number(item.dataset.depth || 0);
        const offsetX = ratioX * depth * 1.2 + globalProgress * depth * 0.8;
        const offsetY = ratioY * depth * 0.8;
        item.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0)`;
      });
    };

    const updateHeroMove = (scrollLeft: number) => {
      const progress = clamp(scrollLeft / heroMoveScrollSpan, 0, 1);
      const easedProgress = 1 - (1 - progress) * (1 - progress);

      if (heroMoveLine) {
        const shift = heroMoveTravel * easedProgress;
        heroMoveLine.style.transform = `translate3d(${shift}px, 0, 0)`;
      }

      if (heroAccentLine) {
        heroAccentLine.style.transform = "translate3d(0, 0, 0)";
        heroAccentLine.style.opacity = "1";
      }

      if (!heroMoveLine) {
        return;
      }
    };

    const updateContinuity = (scrollLeft: number, globalProgress: number) => {
      if (!continuityRail || !continuityBase || !continuityCard || !cardFrontLayer || !cardBackLayer) {
        return;
      }

      const ratioX = pointerXRef.current / Math.max(window.innerWidth, 1) - 0.5;
      const ratioY = pointerYRef.current / Math.max(window.innerHeight, 1) - 0.5;
      const identityStart = identitySection?.offsetLeft ?? viewport.clientWidth;
      const continuitySpan = Math.max(
        identityStart - viewport.clientWidth * 0.08,
        viewport.clientWidth * 0.88,
        1,
      );
      const continuityProgress = clamp(scrollLeft / continuitySpan, 0, 1);
      const continuityEase = 1 - Math.pow(1 - continuityProgress, 1.52);
      const revealDistance = Math.max(viewport.clientWidth * 0.34, 440);
      const revealStart = continuitySpan - revealDistance * 0.46;
      const revealProgress = clamp((scrollLeft - revealStart) / revealDistance, 0, 1);
      const frontImageHold = 0.44;
      const revealUnfoldProgress = clamp(
        (revealProgress - frontImageHold) / Math.max(1 - frontImageHold, 0.0001),
        0,
        1,
      );
      const revealUnfoldEase = 1 - Math.pow(1 - revealUnfoldProgress, 1.9);
      const frontTransitionProgress = Math.pow(revealUnfoldProgress, 1.55);
      const frontTransitionEase = 1 - Math.pow(1 - frontTransitionProgress, 1.38);
      const baseShift = 20 + continuityEase * 72;
      const baseScale = 1.024 + continuityEase * 0.05;
      const basePan = 53 + continuityEase * 10 + ratioX * 5;
      const shellDriftX = ratioX * 4;
      const shellDriftY = ratioY * 2;
      const cardX = ratioX * 2;
      const cardY = -2 + ratioY * 2;
      const frontClipBottom = Math.min(108, frontTransitionEase * 108);
      const frontLift = frontTransitionEase * 4.4;
      const frontOpacity = 1 - clamp((frontTransitionProgress - 0.992) / 0.008, 0, 1);
      const backLift = (1 - revealUnfoldEase) * 1.85;
      const backScale = 1.004 - revealUnfoldEase * 0.004;
      const splitOpacity =
        revealUnfoldProgress <= 0.001 || revealUnfoldProgress >= 0.985
          ? 0
          : 0.18 + (1 - Math.abs(revealUnfoldProgress - 0.5) / 0.5) * 0.52;

      continuityRail.style.transform = `translate3d(${continuityProgress * 1.4}px, 0, 0)`;
      continuityBase.style.backgroundPosition = `${basePan}% center`;
      continuityBase.style.transform = `translate3d(${baseShift}px, 0, 0) scale(${baseScale})`;
      continuityCard.style.transform = `translate3d(${cardX}px, ${cardY}px, 0) scale(${1.012 + continuityEase * 0.012})`;
      continuityCard.style.clipPath = "inset(0 0 0 0)";
      continuityCard.style.opacity = "1";
      continuityCard.style.setProperty("--continuity-split", `${(1 - revealProgress) * 100}%`);
      continuityCard.style.setProperty("--continuity-split-opacity", `${splitOpacity}`);
      cardBackLayer.style.transform = `translate3d(0, ${backLift}%, 0) scale(${backScale})`;
      cardBackLayer.style.opacity = "1";
      cardFrontLayer.style.transform = `translate3d(0, ${-frontLift}%, 0) scale(1.002)`;
      cardFrontLayer.style.clipPath = `inset(0 0 ${frontClipBottom}% 0)`;
      cardFrontLayer.style.opacity = frontOpacity.toFixed(3);

      if (continuityImageShell) {
        continuityImageShell.style.transform = `translate3d(${shellDriftX}px, ${shellDriftY}px, 0)`;
      }
    };

    const updateStoryMedia = (scrollLeft: number) => {
      if (
        !storyPanel ||
        !storyMediaShell ||
        !storyTopOutgoing ||
        !storyMiddleOutgoing ||
        !storyMiddleIncoming
      ) {
        return;
      }

      const panelProgress = (scrollLeft - storyPanel.offsetLeft) / storyPanel.offsetWidth;
      const ratioX = pointerXRef.current / Math.max(window.innerWidth, 1) - 0.5;
      const swapProgress = clamp((panelProgress - 0.01) / 0.72, 0, 1);
      const easedSwap = 1 - Math.pow(1 - swapProgress, 2);
      const incomingLiftProgress = clamp((swapProgress - 0.14) / 0.86, 0, 1);
      const incomingLiftEase = 1 - Math.pow(1 - incomingLiftProgress, 1.8);
      const storyLocalScroll = Math.max(0, scrollLeft - storyPanel.offsetLeft);
      const topOpacity = 1 - clamp((swapProgress - 0.82) / 0.18, 0, 1);
      const middleOpacity = 1 - clamp((swapProgress - 0.74) / 0.26, 0, 1);
      const topFold = 1 - easedSwap * 0.4;
      const middleFold = 1 - easedSwap * 0.42;
      const copyDriftX = easedSwap * 10;
      const copyDriftY = easedSwap * 8;
      const mediaDriftX = easedSwap * 14;
      const imageParallaxX = ratioX * 24 + easedSwap * 26;
      const basePhraseShift = clamp(scrollLeft - storyPhraseStartScroll, 0, storyPhraseTravel);
      const latePhraseCatchup =
        desktopQuery.matches
          ? clamp((panelProgress - 0.74) / 0.18, 0, 1) *
            clamp(viewport.clientWidth * 0.12, 110, 180)
          : 0;
      const phraseShift = basePhraseShift + latePhraseCatchup;
      const accentProgress = clamp((panelProgress - 0.58) / 0.26, 0, 1);
      const accentColor = mixHexColor("#514e37", "#8b8768", accentProgress);

      if (mobileHorizontalQuery.matches) {
        const mediaProgress = clamp((panelProgress + 0.04) / 0.26, 0, 1);
        const copyProgress = clamp((panelProgress - 0.16) / 0.38, 0, 1);
        const mediaEase = 1 - Math.pow(1 - mediaProgress, 1.9);
        const copyEase = 1 - Math.pow(1 - copyProgress, 1.8);
        const copyShiftX = (1 - copyEase) * 74;
        const copyShiftY = (1 - copyEase) * 8;
        const mobilePhraseShift = phraseShift * copyEase;

        storyTopOutgoing.style.transform = `translate3d(${(1 - mediaEase) * 26}px, 0, 0) scale(${0.96 + mediaEase * 0.04})`;
        storyTopOutgoing.style.clipPath = "inset(0 0 0 0)";
        storyTopOutgoing.style.opacity = (0.74 + mediaEase * 0.26).toFixed(3);

        storyMiddleOutgoing.style.transform = `translate3d(${(1 - mediaEase) * 18}px, 0, 0) scale(${0.985 + mediaEase * 0.015})`;
        storyMiddleOutgoing.style.clipPath = "inset(0 0 0 0)";
        storyMiddleOutgoing.style.opacity = "1";

        storyMiddleIncoming.style.transform = `translate3d(${(1 - mediaEase) * 54}px, ${(1 - mediaEase) * 10}%, 0) scale(${1.04 - mediaEase * 0.04})`;
        storyMiddleIncoming.style.opacity = (0.16 + mediaEase * 0.84).toFixed(3);
        storyMediaShell.style.transform = `translate3d(${(1 - mediaEase) * 8}px, 0, 0)`;

        if (storyBottomAccent) {
          storyBottomAccent.style.transform = "translate3d(0, 0, 0) scale(1)";
          storyBottomAccent.style.clipPath = "inset(100% 0 0 0)";
          storyBottomAccent.style.opacity = "0";
        }

        if (storyBaseLayer) {
          storyBaseLayer.style.transform = "translate3d(0, 0, 0) scale(1)";
          storyBaseLayer.style.opacity = "0";
        }

        if (storyCopy) {
          storyCopy.style.opacity = copyEase.toFixed(3);
          storyCopy.style.setProperty("--story-copy-shift-x", `${copyShiftX}px`);
          storyCopy.style.setProperty("--story-copy-shift-y", `${copyShiftY}px`);
          storyCopy.style.setProperty("--story-headline-shift", `${mobilePhraseShift}px`);
          storyCopy.style.setProperty("--story-accent-color", accentColor);
        }

        if (storyHeadlineMotion) {
          storyHeadlineMotion.style.transform = `translate3d(${mobilePhraseShift}px, 0, 0)`;
          storyHeadlineMotion.style.opacity = "1";
        }

        return;
      }

      storyTopOutgoing.style.transform = `translate3d(0, ${-easedSwap * 38}%, 0) scaleY(${topFold})`;
      storyTopOutgoing.style.clipPath = `inset(${easedSwap * 48}% 0 0 0)`;
      storyTopOutgoing.style.opacity = topOpacity.toFixed(3);

      storyMiddleOutgoing.style.transform = `translate3d(0, ${-easedSwap * 14}%, 0) scaleY(${middleFold})`;
      storyMiddleOutgoing.style.clipPath = `inset(${easedSwap * 92}% 0 0 0)`;
      storyMiddleOutgoing.style.opacity = middleOpacity.toFixed(3);

      storyMiddleIncoming.style.transform = `translate3d(${imageParallaxX}px, ${(1 - incomingLiftEase) * 22}%, 0) scale(${1.04 - incomingLiftEase * 0.04})`;
      storyMiddleIncoming.style.opacity = (0.36 + easedSwap * 0.64).toFixed(3);
      storyMediaShell.style.transform = `translate3d(${mediaDriftX}px, ${-easedSwap * 4}px, 0)`;

      if (storyBottomAccent) {
        storyBottomAccent.style.transform = `translate3d(${easedSwap * 16}px, ${(1 - easedSwap) * 42}px, 0) scale(${1 + easedSwap * 0.024})`;
        storyBottomAccent.style.clipPath = `inset(${(1 - easedSwap) * 100}% 0 0 0)`;
        storyBottomAccent.style.opacity = (0.2 + easedSwap * 0.8).toFixed(3);
      }

      if (storyBaseLayer) {
        storyBaseLayer.style.transform = `translate3d(${-easedSwap * 16}px, ${easedSwap * 10}px, 0) scale(${1 + easedSwap * 0.024})`;
        storyBaseLayer.style.opacity = (0.2 + easedSwap * 0.08).toFixed(3);
      }

      if (storyCopy) {
        storyCopy.style.setProperty("--story-copy-shift-x", `${copyDriftX}px`);
        storyCopy.style.setProperty("--story-copy-shift-y", `${copyDriftY}px`);
        storyCopy.style.setProperty("--story-headline-shift", `${phraseShift}px`);
        storyCopy.style.setProperty("--story-accent-color", accentColor);
      }

      if (storyHeadlineMotion) {
        storyHeadlineMotion.style.transform = `translate3d(${phraseShift}px, 0, 0)`;

        if (desktopQuery.matches && motionSection) {
          const phraseRight = storyHeadlineMotion.getBoundingClientRect().right;
          const motionBoundary = motionSection.getBoundingClientRect().left;
          const clearance = motionBoundary - phraseRight;
          const fadeRange = clamp(viewport.clientWidth * 0.085, 96, 144);
          const phraseOpacity = clamp((clearance - 32) / fadeRange, 0, 1);

          storyHeadlineMotion.style.opacity = phraseOpacity.toFixed(3);
        } else {
          storyHeadlineMotion.style.opacity = "1";
        }
      }
    };

    const updateChrome = (scrollLeft: number) => {
      if (!chrome || !identitySection) {
        return;
      }

      const revealPoint = identitySection.offsetLeft + identitySection.offsetWidth * 0.38;
      const shouldShowDesktop = scrollLeft >= revealPoint;
      const shouldShowMobile = identitySection.getBoundingClientRect().top <= window.innerHeight * 0.32;
      const visible = desktopQuery.matches ? shouldShowDesktop : shouldShowMobile;

      root.dataset.chromeVisible = visible ? "true" : "false";

      if (!visible) {
        chromeLogo?.removeAttribute("data-light-coverage");
        chromeMenu?.removeAttribute("data-light-coverage");
        return;
      }

      applyChromeSplitTone(chrome, chromeLogo);
      applyChromeSplitTone(chrome, chromeMenu);
    };

    const updateServicesPin = (scrollLeft: number) => {
      if (!servicesSection || !servicesPinShell) {
        return;
      }

      const start = servicesSection.offsetLeft;
      const totalScrollable = Math.max(servicesSection.offsetWidth - viewport.clientWidth, 1);
      const travel = clamp(scrollLeft - start, 0, totalScrollable);
      servicesPinShell.style.transform = `translate3d(${travel}px, 0, 0)`;
    };

    const update = (scrollLeft: number) => {
      const max = Math.max(maxScroll(), 1);
      const globalProgress = scrollLeft / max;

      root.style.setProperty("--scroll-progress", globalProgress.toFixed(4));
      writeSavedPosition(scrollLeft);
      updateActiveSection();
      syncDocumentVerticalOverflow();

      panels.forEach((panel) => {
        const progress = (scrollLeft - panel.offsetLeft) / panel.offsetWidth;
        panel.style.setProperty("--panel-progress", progress.toFixed(4));
        panel.classList.toggle("is-active", progress > -0.42 && progress < 0.52);
        panel.classList.toggle("is-peek", progress > -0.7 && progress < 0.82);
      });

      parallaxItems.forEach((item) => {
        const panel = item.closest<HTMLElement>("[data-panel]");

        if (!panel) {
          return;
        }

        const progress = (scrollLeft - panel.offsetLeft) / panel.offsetWidth;
        const speed = Number(item.dataset.parallaxSpeed || 0);
        item.style.transform = `translate3d(${progress * speed}px, 0, 0)`;
      });

      updateDepth(globalProgress);
      updateHeroMove(scrollLeft);
      updateContinuity(scrollLeft, globalProgress);
      updateStoryMedia(scrollLeft);
      updateChrome(scrollLeft);
      updateServicesPin(scrollLeft);
    };

    const stopTick = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const tick = () => {
      const horizontalDistance = targetScrollRef.current - currentScrollRef.current;
      const horizontalEasing =
        Math.abs(horizontalDistance) > viewport.clientWidth * 0.22
          ? HORIZONTAL_LONG_TRAVEL_EASING
          : HORIZONTAL_SHORT_TRAVEL_EASING;

      const verticalDistance =
        targetVerticalScrollRef.current - currentVerticalScrollRef.current;
      const verticalEasing =
        Math.abs(verticalDistance) > window.innerHeight * 0.18
          ? VERTICAL_LONG_TRAVEL_EASING
          : VERTICAL_SHORT_TRAVEL_EASING;

      if (Math.abs(horizontalDistance) > 0.15) {
        currentScrollRef.current += horizontalDistance * horizontalEasing;

        if (Math.abs(targetScrollRef.current - currentScrollRef.current) < 0.12) {
          currentScrollRef.current = targetScrollRef.current;
        }

        viewport.scrollLeft = currentScrollRef.current;
        update(currentScrollRef.current);
      }

      if (Math.abs(verticalDistance) > 0.2) {
        currentVerticalScrollRef.current += verticalDistance * verticalEasing;

        if (Math.abs(targetVerticalScrollRef.current - currentVerticalScrollRef.current) < 0.2) {
          currentVerticalScrollRef.current = targetVerticalScrollRef.current;
        }

        scrollWindowTo(currentVerticalScrollRef.current);
      }

      if (
        Math.abs(targetScrollRef.current - currentScrollRef.current) > 0.15 ||
        Math.abs(targetVerticalScrollRef.current - currentVerticalScrollRef.current) > 0.2
      ) {
        rafRef.current = window.requestAnimationFrame(tick);
        return;
      }

      stopTick();
    };

    const start = () => {
      if (!rafRef.current) {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };

    const snapToScroll = (value: number) => {
      stopTick();
      currentScrollRef.current = value;
      targetScrollRef.current = value;
      viewport.scrollLeft = value;
      update(value);
    };

    const scrollWindowTo = (top: number) => {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      window.scrollTo({ top, behavior: "auto" });
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    };

    const setVerticalScroll = (nextY: number) => {
      const clampedY = clamp(nextY, 0, maxVerticalScroll());
      currentVerticalScrollRef.current = clampedY;
      targetVerticalScrollRef.current = clampedY;
      scrollWindowTo(clampedY);
      return clampedY;
    };

    const setVerticalTarget = (nextY: number) => {
      targetVerticalScrollRef.current = clamp(nextY, 0, maxVerticalScroll());
      syncDocumentVerticalOverflow();
      start();
    };

    const resetToHero = () => {
      stopTick();
      setVerticalScroll(0);
      snapToScroll(0);
      viewport.scrollLeft = 0;
      currentScrollRef.current = 0;
      targetScrollRef.current = 0;
      update(0);
      syncDocumentVerticalOverflow();
    };

    const getAdaptiveWheelDelta = (delta: number) => {
      const now = performance.now();
      const direction = Math.sign(delta) || 1;
      const elapsed =
        lastWheelTimestampRef.current > 0
          ? now - lastWheelTimestampRef.current
          : Number.POSITIVE_INFINITY;
      const cadenceRatio = clamp(
        (WHEEL_CADENCE_WINDOW_MS - elapsed) / WHEEL_CADENCE_WINDOW_MS,
        0,
        1,
      );
      const cadenceMultiplier = 1 + cadenceRatio * WHEEL_CADENCE_BOOST;
      const directionMultiplier =
        lastWheelDirectionRef.current === direction && elapsed <= WHEEL_DIRECTION_WINDOW_MS
          ? WHEEL_DIRECTION_BOOST
          : 1;

      lastWheelTimestampRef.current = now;
      lastWheelDirectionRef.current = direction;

      return delta * cadenceMultiplier * directionMultiplier;
    };

    const queueVerticalDelta = (delta: number) => {
      currentVerticalScrollRef.current = window.scrollY;
      const base =
        delta >= 0
          ? Math.max(window.scrollY, currentVerticalScrollRef.current, targetVerticalScrollRef.current)
          : Math.min(window.scrollY, currentVerticalScrollRef.current, targetVerticalScrollRef.current);

      setVerticalTarget(base + delta * VERTICAL_WHEEL_MULTIPLIER);
    };

    const syncDocumentVerticalOverflow = () => {
      if (mobileHorizontalQuery.matches) {
        if (isMenuOpen()) {
          document.documentElement.style.overflowY = "hidden";
          document.body.style.overflowY = "hidden";
          return;
        }

        const max = maxScroll();
        const allowVerticalScroll =
          window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD ||
          viewport.scrollLeft >= max - HANDOFF_SNAP_DISTANCE;

        document.documentElement.style.overflowY = allowVerticalScroll ? "auto" : "hidden";
        document.body.style.overflowY = allowVerticalScroll ? "auto" : "hidden";
        return;
      }

      if (!desktopQuery.matches) {
        document.documentElement.style.overflowY = "";
        document.body.style.overflowY = "";
        return;
      }

      if (isMenuOpen()) {
        document.documentElement.style.overflowY = "hidden";
        document.body.style.overflowY = "hidden";
        return;
      }

      const allowVerticalScrollbar =
        window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD ||
        targetVerticalScrollRef.current > VERTICAL_SCROLL_LOCK_THRESHOLD;
      document.documentElement.style.overflowY = allowVerticalScrollbar ? "auto" : "hidden";
      document.body.style.overflowY = allowVerticalScrollbar ? "auto" : "hidden";
    };

    const restoreSavedPosition = () => {
      const shouldResetToHero = consumeHomeHeroResetRequest();
      const saved = readSavedPosition();
      const max = maxScroll();

      if (!saved || !desktopQuery.matches || shouldResetToHero) {
        const initialScrollLeft = shouldResetToHero ? 0 : clamp(viewport.scrollLeft, 0, max);

        currentScrollRef.current = initialScrollLeft;
        targetScrollRef.current = currentScrollRef.current;
        viewport.scrollLeft = currentScrollRef.current;
        currentVerticalScrollRef.current = window.scrollY;
        targetVerticalScrollRef.current = window.scrollY;
        update(currentScrollRef.current);
        return;
      }

      const savedLeft = Number(saved.left || 0);
      const savedProgress = Number(saved.progress || 0);
      const savedViewportWidth = Number(saved.viewportWidth || 0);
      const restored = Math.abs(savedViewportWidth - viewport.clientWidth) < 8
        ? savedLeft
        : savedProgress * max;

      currentScrollRef.current = clamp(restored, 0, max);
      targetScrollRef.current = currentScrollRef.current;
      viewport.scrollLeft = currentScrollRef.current;
      currentVerticalScrollRef.current = window.scrollY;
      targetVerticalScrollRef.current = window.scrollY;
      hasRestoredPosition = true;
      update(currentScrollRef.current);
      syncDocumentVerticalOverflow();
    };

    const handleWheel = (event: WheelEvent) => {
      if ((!desktopQuery.matches && !mobileHorizontalQuery.matches) || isMenuOpen()) {
        return;
      }

      const max = maxScroll();
      const delta = normalizeWheelDelta(event);

      if (Math.abs(delta) < 0.1) {
        return;
      }

      const adaptiveDelta = getAdaptiveWheelDelta(delta);

      if (window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD) {
        event.preventDefault();
        queueVerticalDelta(adaptiveDelta);
        return;
      }

      const current = currentScrollRef.current;
      const target = targetScrollRef.current;
      const nearMax =
        max - Math.max(current, target) <= HANDOFF_SNAP_DISTANCE;
      const nearMin = Math.min(current, target) <= HANDOFF_SNAP_DISTANCE;

      if (delta > 0 && nearMax) {
        event.preventDefault();

        if (current < max || target < max) {
          const remainingHorizontalDistance = Math.max(0, max - Math.max(current, target));
          const spillDelta =
            adaptiveDelta - remainingHorizontalDistance / HORIZONTAL_WHEEL_MULTIPLIER;

          snapToScroll(max);

          if (spillDelta > WHEEL_HANDOFF_SPILL_THRESHOLD) {
            queueVerticalDelta(spillDelta);
          }

          return;
        }

        queueVerticalDelta(adaptiveDelta);
        return;
      }

      if (delta < 0 && nearMin) {
        if (current > 0 || target > 0) {
          event.preventDefault();
          snapToScroll(0);
        }

        return;
      }

      event.preventDefault();
      targetScrollRef.current = clamp(
        target + adaptiveDelta * HORIZONTAL_WHEEL_MULTIPLIER,
        0,
        max,
      );
      start();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (isMenuOpen()) {
        return;
      }

      if (isInteractiveTarget(event.target)) {
        return;
      }

      if (mobileHorizontalQuery.matches && event.pointerType === "touch") {
        if (window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD) {
          return;
        }

        touchDragging = true;
        touchAxis = "undecided";
        touchLastX = event.clientX;
        touchLastY = event.clientY;
        updateCursor(event.clientX, event.clientY);
        viewport.setPointerCapture(event.pointerId);
        return;
      }

      if (!desktopQuery.matches && !mobileHorizontalQuery.matches) {
        return;
      }

      dragging = true;
      dragStartX = event.clientX;
      dragStartTarget = targetScrollRef.current;
      viewport.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: PointerEvent) => {
      updateCursor(event.clientX, event.clientY);

      if (mobileHorizontalQuery.matches && event.pointerType === "touch" && touchDragging) {
        if (isMenuOpen() || window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD) {
          return;
        }

        const deltaX = event.clientX - touchLastX;
        const deltaY = event.clientY - touchLastY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);

        if (touchAxis === "undecided") {
          if (Math.max(absX, absY) < 4) {
            return;
          }

          touchAxis = absY >= absX ? "vertical" : "horizontal";
        }

        const gestureDelta = touchAxis === "vertical" ? -deltaY : -deltaX;

        if (Math.abs(gestureDelta) < 0.1) {
          touchLastX = event.clientX;
          touchLastY = event.clientY;
          return;
        }

        const max = maxScroll();
        const current = viewport.scrollLeft;
        const atStart = current <= 0.5;
        const atEnd = current >= max - 0.5;
        const movingForward = gestureDelta > 0;
        const movingBackward = gestureDelta < 0;

        if (movingForward && atEnd) {
          event.preventDefault();
          queueVerticalDelta(Math.max(gestureDelta, 0) * 1.04);
          touchLastX = event.clientX;
          touchLastY = event.clientY;
          return;
        }

        if (movingBackward && atStart) {
          touchLastX = event.clientX;
          touchLastY = event.clientY;
          return;
        }

        event.preventDefault();
        stopTick();

        const nextLeft = clamp(current + gestureDelta * 1.18, 0, max);
        viewport.scrollLeft = nextLeft;
        currentScrollRef.current = nextLeft;
        targetScrollRef.current = nextLeft;
        update(nextLeft);

        touchLastX = event.clientX;
        touchLastY = event.clientY;
        return;
      }

      if (!desktopQuery.matches && !mobileHorizontalQuery.matches) {
        return;
      }

      if (!dragging || isMenuOpen()) {
        return;
      }

      targetScrollRef.current = clamp(
        dragStartTarget - (event.clientX - dragStartX) * 1.4,
        0,
        maxScroll(),
      );
      start();
    };

    const handlePointerUp = () => {
      dragging = false;
      touchDragging = false;
      touchAxis = "undecided";
    };

    const handleTouchStart = (event: TouchEvent) => {
      if (typeof window.PointerEvent !== "undefined") {
        return;
      }

      if (!mobileHorizontalQuery.matches || isMenuOpen() || window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD) {
        return;
      }

      if (event.touches.length !== 1 || isInteractiveTarget(event.target)) {
        return;
      }

      const touch = event.touches[0];
      touchDragging = true;
      touchAxis = "undecided";
      touchLastX = touch.clientX;
      touchLastY = touch.clientY;
      updateCursor(touch.clientX, touch.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (typeof window.PointerEvent !== "undefined") {
        return;
      }

      if (
        !mobileHorizontalQuery.matches ||
        !touchDragging ||
        isMenuOpen() ||
        window.scrollY > VERTICAL_SCROLL_LOCK_THRESHOLD ||
        event.touches.length !== 1
      ) {
        return;
      }

      const touch = event.touches[0];
      const deltaX = touch.clientX - touchLastX;
      const deltaY = touch.clientY - touchLastY;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      updateCursor(touch.clientX, touch.clientY);

      if (touchAxis === "undecided") {
        if (Math.max(absX, absY) < 4) {
          return;
        }

        touchAxis = absY >= absX ? "vertical" : "horizontal";
      }

      const gestureDelta = touchAxis === "vertical" ? -deltaY : -deltaX;

      if (Math.abs(gestureDelta) < 0.1) {
        touchLastX = touch.clientX;
        touchLastY = touch.clientY;
        return;
      }

      const max = maxScroll();
      const current = viewport.scrollLeft;
      const atStart = current <= 0.5;
      const atEnd = current >= max - 0.5;
      const movingForward = gestureDelta > 0;
      const movingBackward = gestureDelta < 0;

      if (movingForward && atEnd) {
        event.preventDefault();
        queueVerticalDelta(Math.max(gestureDelta, 0) * 1.04);
        touchLastX = touch.clientX;
        touchLastY = touch.clientY;
        return;
      }

      if (movingBackward && atStart) {
        touchLastX = touch.clientX;
        touchLastY = touch.clientY;
        return;
      }

      event.preventDefault();
      stopTick();

      const nextLeft = clamp(current + gestureDelta * 1.18, 0, max);
      viewport.scrollLeft = nextLeft;
      currentScrollRef.current = nextLeft;
      targetScrollRef.current = nextLeft;
      update(nextLeft);

      touchLastX = touch.clientX;
      touchLastY = touch.clientY;
    };

    const handleTouchEnd = () => {
      if (typeof window.PointerEvent !== "undefined") {
        return;
      }

      touchDragging = false;
      touchAxis = "undecided";
    };

    const handleResize = () => {
      targetScrollRef.current = clamp(targetScrollRef.current, 0, maxScroll());
      currentScrollRef.current = clamp(currentScrollRef.current, 0, maxScroll());
      targetVerticalScrollRef.current = clamp(targetVerticalScrollRef.current, 0, maxVerticalScroll());
      currentVerticalScrollRef.current = clamp(window.scrollY, 0, maxVerticalScroll());
      viewport.scrollLeft = currentScrollRef.current;
      measureHeroMove();
      measureStoryPhrase();
      syncDocumentVerticalOverflow();

      if (desktopQuery.matches) {
        update(currentScrollRef.current);
      } else {
        updateActiveSection();
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (!desktopQuery.matches || isMenuOpen()) {
        return;
      }

      const movingForward =
        ["ArrowRight", "ArrowDown", "PageDown"].includes(event.key) ||
        (event.key === " " && !event.shiftKey);
      const movingBackward =
        ["ArrowLeft", "ArrowUp", "PageUp"].includes(event.key) ||
        (event.key === " " && event.shiftKey);

      if (!movingForward && !movingBackward) {
        return;
      }

      event.preventDefault();

      const horizontalStep = window.innerWidth * KEYBOARD_SCROLL_STEP_RATIO;
      const verticalStep = window.innerHeight * KEYBOARD_SCROLL_STEP_RATIO;
      const furthestVerticalPosition = Math.max(
        window.scrollY,
        currentVerticalScrollRef.current,
        targetVerticalScrollRef.current,
      );

      if (
        movingBackward &&
        furthestVerticalPosition > VERTICAL_SCROLL_LOCK_THRESHOLD &&
        targetVerticalScrollRef.current <= VERTICAL_SCROLL_LOCK_THRESHOLD
      ) {
        const max = maxScroll();
        snapToScroll(max);
        setVerticalScroll(0);
        targetScrollRef.current = Math.max(0, max - horizontalStep);
        start();
        return;
      }

      if (furthestVerticalPosition > VERTICAL_SCROLL_LOCK_THRESHOLD) {
        const verticalPosition = movingForward
          ? furthestVerticalPosition
          : Math.min(
              window.scrollY,
              currentVerticalScrollRef.current,
              targetVerticalScrollRef.current,
            );

        if (movingForward) {
          setVerticalTarget(verticalPosition + verticalStep);
          return;
        }

        const nextVerticalPosition = Math.max(0, verticalPosition - verticalStep);

        if (nextVerticalPosition <= VERTICAL_SCROLL_LOCK_THRESHOLD) {
          snapToScroll(maxScroll());
          setVerticalTarget(0);
          return;
        }

        setVerticalTarget(nextVerticalPosition);
        return;
      }

      const max = maxScroll();
      const horizontalPosition = Math.max(
        viewport.scrollLeft,
        currentScrollRef.current,
        targetScrollRef.current,
      );

      if (movingForward && max - horizontalPosition <= HANDOFF_SNAP_DISTANCE) {
        snapToScroll(max);
        setVerticalTarget(verticalStep);
        return;
      }

      targetScrollRef.current = clamp(
        targetScrollRef.current + (movingForward ? horizontalStep : -horizontalStep),
        0,
        max,
      );
      if (movingBackward) {
        setVerticalScroll(0);
      }
      start();
    };

    const handleViewportScroll = () => {
      if (!isHorizontalViewportMode() || rafRef.current) {
        return;
      }

      currentScrollRef.current = viewport.scrollLeft;
      targetScrollRef.current = viewport.scrollLeft;
      update(viewport.scrollLeft);
    };

    const handleMediaChange = () => {
      stopTick();
      currentScrollRef.current = clamp(viewport.scrollLeft, 0, maxScroll());
      targetScrollRef.current = currentScrollRef.current;
      currentVerticalScrollRef.current = clamp(window.scrollY, 0, maxVerticalScroll());
      targetVerticalScrollRef.current = currentVerticalScrollRef.current;
      measureHeroMove();
      measureStoryPhrase();
      syncDocumentVerticalOverflow();

      if (desktopQuery.matches) {
        update(currentScrollRef.current);
        return;
      }

      root.style.setProperty("--scroll-progress", "0");
      viewport.scrollLeft = 0;
      currentScrollRef.current = 0;
      targetScrollRef.current = 0;
      updateActiveSection();
    };

    // applySplitTone only ran from the horizontal-scroll update() loop, so once
    // the wheel handoff releases into native vertical scroll (past the pinned
    // stage, into the footer) the split-tone clip-path froze at whatever it last
    // sampled — showing a stale, half-clipped remnant of the previous background
    // instead of tracking what's actually behind the (now fixed) chrome.
    const handleWindowScroll = () => {
      if (!rafRef.current) {
        currentVerticalScrollRef.current = window.scrollY;
        targetVerticalScrollRef.current = window.scrollY;
      }

      syncDocumentVerticalOverflow();
      updateActiveSection();

      if (chrome && root.dataset.chromeVisible === "true") {
        applyChromeSplitTone(chrome, chromeLogo);
        applyChromeSplitTone(chrome, chromeMenu);
      }
    };

    const bindMobileObserver = () => {
      if (!("IntersectionObserver" in window) || desktopQuery.matches) {
        panels.forEach((panel, index) => {
          panel.classList.toggle("is-active", index === 0);
        });
        return;
      }

      mobileObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle("is-active", entry.isIntersecting);
          });
        },
        { threshold: 0.45 },
      );

      panels.forEach((panel) => mobileObserver?.observe(panel));
    };

    const unbindMagnetic = bindMagneticLinks(root);

    // The stage should keep ownership of wheel navigation even when the cursor
    // drifts over fixed chrome or the footer handoff area, otherwise the
    // horizontal/vertical transition feels like it randomly "drops" scroll.
    window.addEventListener("wheel", handleWheel, { passive: false });
    viewport.addEventListener("pointerdown", handlePointerDown);
    viewport.addEventListener("pointermove", handlePointerMove);
    viewport.addEventListener("pointerup", handlePointerUp);
    viewport.addEventListener("pointercancel", handlePointerUp);
    viewport.addEventListener("mouseleave", handlePointerUp);
    viewport.addEventListener("touchstart", handleTouchStart, { passive: true });
    viewport.addEventListener("touchmove", handleTouchMove, { passive: false });
    viewport.addEventListener("touchend", handleTouchEnd);
    viewport.addEventListener("touchcancel", handleTouchEnd);
    viewport.addEventListener("scroll", handleViewportScroll, { passive: true });
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    window.addEventListener(HOMEPAGE_CONTENT_LOADED_EVENT, handleResize);
    window.addEventListener("keydown", handleKeydown);
    desktopQuery.addEventListener("change", handleMediaChange);
    mobileHorizontalQuery.addEventListener("change", handleMediaChange);

    bindMobileObserver();
    measureHeroMove();
    measureStoryPhrase();
    updateCursor(pointerXRef.current, pointerYRef.current);
    restoreSavedPosition();
    syncDocumentVerticalOverflow();
    window.addEventListener(HOME_HERO_RESET_EVENT, resetToHero);

    if (!hasRestoredPosition && desktopQuery.matches) {
      update(currentScrollRef.current);
    }

    return () => {
      stopTick();
      mobileObserver?.disconnect();
      unbindMagnetic();
      window.removeEventListener("wheel", handleWheel);
      viewport.removeEventListener("pointerdown", handlePointerDown);
      viewport.removeEventListener("pointermove", handlePointerMove);
      viewport.removeEventListener("pointerup", handlePointerUp);
      viewport.removeEventListener("pointercancel", handlePointerUp);
      viewport.removeEventListener("mouseleave", handlePointerUp);
      viewport.removeEventListener("touchstart", handleTouchStart);
      viewport.removeEventListener("touchmove", handleTouchMove);
      viewport.removeEventListener("touchend", handleTouchEnd);
      viewport.removeEventListener("touchcancel", handleTouchEnd);
      viewport.removeEventListener("scroll", handleViewportScroll);
      window.removeEventListener("scroll", handleWindowScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener(HOMEPAGE_CONTENT_LOADED_EVENT, handleResize);
      window.removeEventListener("keydown", handleKeydown);
      window.removeEventListener(HOME_HERO_RESET_EVENT, resetToHero);
      desktopQuery.removeEventListener("change", handleMediaChange);
      mobileHorizontalQuery.removeEventListener("change", handleMediaChange);
      document.documentElement.style.overflowY = "";
      document.body.style.overflowY = "";
    };
  }, [sectionIds, sectionSelector]);

  const scrollToSection = (sectionId: string) => {
    const viewport = viewportRef.current;
    const target = document.getElementById(sectionId);

    if (!target || !viewport) {
      return;
    }

    if (
      (window.matchMedia(DESKTOP_MEDIA).matches ||
        window.matchMedia(MOBILE_HORIZONTAL_STAGE_MEDIA).matches) &&
      sectionIds.includes(sectionId)
    ) {
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const nextLeft = Math.min(Math.max(target.offsetLeft, 0), max);

      if (window.matchMedia(MOBILE_HORIZONTAL_STAGE_MEDIA).matches) {
        viewport.scrollTo({
          left: nextLeft,
          behavior: "smooth",
        });
        currentScrollRef.current = nextLeft;
        targetScrollRef.current = nextLeft;
        return;
      }

      targetScrollRef.current = nextLeft;
      currentScrollRef.current = nextLeft;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }

      viewport.scrollLeft = nextLeft;
      window.requestAnimationFrame(() => {
        viewport.scrollLeft = nextLeft;
        currentScrollRef.current = nextLeft;
        targetScrollRef.current = nextLeft;
      });

      return;
    }

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return {
    viewportRef,
    activeSectionId,
    scrollToSection,
  };
}
